import React, { useContext, useEffect } from 'react';
import { Button } from '@bluebeam/react-skyline-button';
import { useTranslator } from '@bluebeam/react-i18n';
import { BannerContext } from 'components/Routes/BannerContext';
import AppStoreContext from 'contexts/AppStoreContext';
import { DateTime } from 'luxon';
import { useUserInfo } from 'providers/UserInfoProvider';

interface SubscriptionExpirationBannerProps {
  onProjectPage: boolean;
}

const SubscriptionExpirationBanner: React.VFC<
  SubscriptionExpirationBannerProps
> = ({ onProjectPage }) => {
  const bannerRef = React.useRef<HTMLDivElement>(null);
  const { translate } = useTranslator([
    'subscriptionExpirationBanner',
    'freshStartSubscriptionBanner',
  ]);
  const { state: appState } = useContext(AppStoreContext);
  const { setHeight } = useContext(BannerContext);
  const userInfo = useUserInfo();
  const {
    isTrial,
    subscriptionExpiration,
    package: licensePackage,
  } = userInfo.getLicense();
  const user = userInfo.getCurrentUser();

  const claimedRole = appState.roles.getIn([user.id, 'role']);
  const assignedRole = appState.roles.getIn([user.id, 'assignedRole']);

  const showsRoleRestrictedBanner =
    onProjectPage && claimedRole !== assignedRole;

  const isPaidSubscription =
    !isTrial && new Set(['Core', 'Complete', 'Basics']).has(licensePackage);

  const isUnpaidSubscription = !isTrial && licensePackage === 'Unpaid';
  const isInvalidSubscriptionExpirationDate =
    Date.parse(subscriptionExpiration) < 0;

  const paidSubscriptionSecondsRemaining = Math.max(
    Math.floor(
      DateTime.fromISO(subscriptionExpiration).diffNow('seconds').seconds
    ),
    0
  );

  const isPaidSubscriptionExpired = paidSubscriptionSecondsRemaining <= 0;

  const handleBuyButtonClick = () => {
    window.open(process.env.BB_SUBSCRIPTION_PATH, '_blank');
  };

  const shouldRenderBannerForPaidPackage =
    isPaidSubscription &&
    isPaidSubscriptionExpired &&
    !showsRoleRestrictedBanner;

  const shouldRenderBannerForUnpaidPackage =
    isUnpaidSubscription && !isInvalidSubscriptionExpirationDate;

  return shouldRenderBannerForPaidPackage ||
    shouldRenderBannerForUnpaidPackage ? (
    <div
      className="h-12 row justify-center items-center w100 bb-2 bgd-light-grey-2 size-16"
      ref={bannerRef}
    >
      <span>{translate('subscriptionExpirationBanner:expiredContent')}</span>
      <Button
        className="ml4"
        size="32px"
        variant="outline"
        onClick={handleBuyButtonClick}
      >
        {translate('freshStartSubscriptionBanner:buyNow')}
      </Button>
    </div>
  ) : null;
};

export default SubscriptionExpirationBanner;
